import React, { useRef, useMemo, useState } from 'react';
import { DialogMenu } from './dialog-menu';
import { PinturaEditor } from "@pqina/react-pintura";
import { getEditorDefaults, PinturaDefaultImageWriterResult } from '@pqina/pintura';
import '@pqina/pintura/pintura.css';
import cachedImageElement from "../../lib/cached-image-element";
import { UploadedFile } from "../../api/file_api";

interface CropAndPositionPopoverProps {
  uploadFile?: (file: File) => Promise<UploadedFile>;
  src: string;
  onSave?: (imageData: File) => void;
  trigger?: React.ReactNode;
}

const editorDefaults = getEditorDefaults();

export const CropAndPositionPopover: React.FC<CropAndPositionPopoverProps> = ({
  uploadFile,
  src,
  onSave,
  trigger,
}) => {
  const [open, setOpen] = React.useState(false);
  const [imageElement, setImageElement] = useState<HTMLImageElement | undefined>(src ? cachedImageElement(src) : undefined);
  const editorRef = useRef<PinturaEditor>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Update currentSrc when src prop changes
  React.useEffect(() => {
    setImageElement(cachedImageElement(src));
  }, [src]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploading(true);
      if (uploadFile) {
        const response = await uploadFile(file);
        const permalink = response?.permalink;
        permalink && setImageElement(cachedImageElement(permalink));
      }

      // Reset the file input
      event.target.value = '';
      setUploading(false);
    }
  };

  const handleEditorProcess = useMemo(() => (imageState: PinturaDefaultImageWriterResult) => {
    if (onSave) {
      // Create a files list
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(imageState.dest);

      // Assign new files
      onSave(dataTransfer.files[0]);
      setOpen(false);
    }
  }, [src, onSave]);

  const process = useMemo(() => () => {
    const { editor } = editorRef.current || {};
    if (editor) {
      editor.processImage();
    }
  }, [editorRef.current, onSave]);

  return (
    <DialogMenu
      trigger={trigger}
      open={open}
      onOpenChange={setOpen}
      title="Crop & Position"
      className="max-w-[95vw] h-[480px] md:w-[500px]"
      contentClassName={"p-4 pb-0 md:pb-4 md:pt-10 md:max-w-[700px]"}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <div className="flex flex-col gap-2">
      <div className="w-full h-[420px] max-h-[60dvh]">
        <PinturaEditor
          ref={editorRef}
          {...editorDefaults}
          enableToolbar={false}
          enableUtils={false}
          src={imageElement}
          onProcess={handleEditorProcess}
          onClose={() => setOpen(false)}
        />
      </div>
      <div className="flex flex-col md:flex-row min-w-[300px] gap-2 items-center justify-between">
        <div className="flex flex-row gap-4">
          <button 
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-8 rounded"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
          <button 
          className="min-w-[120px] w-full md:w-auto bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          onClick={process}
        >
          Save
        </button>
        </div>
        
      </div>
      </div>
    </DialogMenu>
  );
};

export default CropAndPositionPopover; 